import { useEffect, useState, useCallback, useMemo } from "react";
import {
  LogOut, Lock, Users, Inbox, ShoppingBag, DollarSign, RefreshCw, Phone, Mail, Loader2,
  Search, Download, ArrowUpDown, ChevronUp, ChevronDown, StickyNote, Clock, Truck,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, formatUSD, ADMIN_TOKEN_KEY } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AccountDialog } from "@/components/admin/AccountDialog";
import { OrderDetailDialog } from "@/components/admin/OrderDetailDialog";
import { SalesSnapshot } from "@/components/admin/SalesSnapshot";
import { MarketingInsights } from "@/components/admin/MarketingInsights";

const STATUS_STYLE = {
  new: "bg-brand-red/10 text-brand-red border-brand-red/20",
  contacted: "bg-amber-100 text-amber-700 border-amber-200",
  closed: "bg-emerald-100 text-emerald-700 border-emerald-200",
};
const PAY_STYLE = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  initiated: "bg-slate-100 text-slate-600",
  expired: "bg-slate-100 text-slate-500",
  failed: "bg-brand-red/10 text-brand-red",
};
const SHIP_STYLE = {
  processing: "bg-amber-100 text-amber-700",
  shipped: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
};
const SHIP_OPTS = [
  { value: "processing", label: "Processing" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    ", " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const STATUS_OPTS_LEADS = [
  { value: "all", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];
const STATUS_OPTS_ORDERS = [
  { value: "all", label: "All payments" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "initiated", label: "Initiated" },
  { value: "expired", label: "Expired" },
  { value: "failed", label: "Failed" },
];
const DATE_OPTS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const withinRange = (iso, range) => {
  if (range === "all" || !iso) return true;
  const t = new Date(iso).getTime();
  if (range === "today") {
    const s = new Date();
    s.setHours(0, 0, 0, 0);
    return t >= s.getTime();
  }
  const days = range === "7d" ? 7 : 30;
  return t >= Date.now() - days * 86400000;
};

const getSortVal = (row, key) => {
  if (key === "created_at") return new Date(row.created_at || 0).getTime();
  if (key === "amount") return row.amount || 0;
  return (row[key] || "").toString().toLowerCase();
};
const sortRows = (rows, sort) => {
  if (!sort.key) return rows;
  const arr = [...rows].sort((a, b) => {
    const av = getSortVal(a, sort.key), bv = getSortVal(b, sort.key);
    if (av < bv) return -1;
    if (av > bv) return 1;
    return 0;
  });
  return sort.dir === "desc" ? arr.reverse() : arr;
};

const noteToStr = (l) => (l.note_history || []).map((n) => `${fmtDate(n.at)}: ${n.text}`).join(" | ");

const downloadCSV = (filename, headers, rows) => {
  const esc = (v) => `"${(v == null ? "" : String(v)).replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

function SortHeader({ label, sortKey, sort, onSort, testid }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <TableHead>
      <button
        data-testid={testid}
        onClick={() => onSort(sortKey)}
        className="btn-focus inline-flex items-center gap-1.5 font-bold text-brand-navy hover:text-brand-red"
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? "text-brand-red" : "text-brand-ink2/50"}`} />
      </button>
    </TableHead>
  );
}

function FilterBar({ prefix, search, setSearch, status, setStatus, statusOptions, dateRange, setDateRange, onExport, count, total, placeholder }) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
      <div className="relative flex-1 lg:max-w-xs">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          data-testid={`${prefix}-search`}
          className="pl-10 h-12 rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy"
        />
      </div>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid={`${prefix}-status-filter`} className="w-full lg:w-[168px] h-12 rounded-xl border-brand-line font-semibold text-brand-navy">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={dateRange} onValueChange={setDateRange}>
        <SelectTrigger data-testid={`${prefix}-date-filter`} className="w-full lg:w-[150px] h-12 rounded-xl border-brand-line font-semibold text-brand-navy">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="text-sm font-semibold text-brand-ink2 lg:ml-1 whitespace-nowrap" data-testid={`${prefix}-count`}>
        {count} of {total}
      </span>
      <button
        onClick={onExport}
        data-testid={`${prefix}-export`}
        className="btn-focus lg:ml-auto inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl bg-brand-navy text-white font-bold hover:bg-brand-navy-700 transition-colors"
      >
        <Download className="w-4 h-4" /> Export CSV
      </button>
    </div>
  );
}

function NoteTimeline({ lead, onAdd }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const history = lead.note_history || [];
  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    await onAdd(t);
    setSaving(false);
    setText("");
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`lead-notes-btn-${lead.id}`}
          className="btn-focus inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-brand-line text-sm font-semibold text-brand-navy hover:border-brand-navy hover:bg-brand-bg transition-colors"
        >
          <StickyNote className="w-4 h-4 text-brand-red" />
          {history.length ? `Notes (${history.length})` : "Add note"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy">Note history — {lead.name}</DialogTitle>
          <DialogDescription className="text-brand-ink2">Timestamped notes for this lead. Add a new entry below.</DialogDescription>
        </DialogHeader>
        <div data-testid={`lead-notes-timeline-${lead.id}`} className="max-h-64 overflow-y-auto space-y-3 pr-1">
          {history.length === 0 && (
            <p className="text-brand-ink2 text-sm py-4 text-center">No notes yet. Add the first one below.</p>
          )}
          {history.map((n, i) => (
            <div key={n.id || i} className="relative rounded-xl bg-brand-bg border border-brand-line p-3.5 pl-4">
              <span className="absolute left-0 top-3.5 bottom-3.5 w-1 rounded-full bg-brand-red" />
              <p className="text-brand-navy whitespace-pre-wrap leading-relaxed">{n.text}</p>
              <p className="mt-2 text-xs font-semibold text-brand-ink2 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> {fmtDate(n.at)}
              </p>
            </div>
          ))}
        </div>
        <Textarea
          data-testid={`lead-notes-input-${lead.id}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note to the timeline…"
          className="min-h-[80px] rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy"
        />
        <DialogFooter>
          <button
            onClick={add}
            disabled={saving || !text.trim()}
            data-testid={`lead-notes-save-${lead.id}`}
            className="btn-focus inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors disabled:opacity-50"
          >
            {saving ? "Adding…" : "Add to timeline"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShipDialog({ order, onSave }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(order.ship_status || "processing");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setStatus(order.ship_status || "processing");
      setTracking(order.tracking_number || "");
      setNotify(false);
    }
  }, [open, order]);
  const save = async () => {
    setSaving(true);
    await onSave({ ship_status: status, tracking_number: tracking, notify });
    setSaving(false);
    setOpen(false);
  };
  const st = order.ship_status || "processing";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`order-ship-btn-${order.session_id}`}
          className="btn-focus inline-flex items-center gap-2 px-3 h-9 rounded-full border border-brand-line hover:border-brand-navy hover:bg-brand-bg transition-colors"
        >
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${SHIP_STYLE[st]}`}>{st}</span>
          <Truck className="w-4 h-4 text-brand-ink2" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy">Fulfillment</DialogTitle>
          <DialogDescription className="text-brand-ink2">Update shipping status and tracking. Marking as Shipped emails the customer automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">Shipping status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid={`order-ship-status-${order.session_id}`} className="h-12 rounded-xl border-brand-line font-semibold text-brand-navy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHIP_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">Tracking number</Label>
            <Input
              data-testid={`order-tracking-${order.session_id}`}
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              placeholder="e.g. 1Z999AA10123456784"
              className="h-12 rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy"
            />
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              data-testid={`order-notify-${order.session_id}`}
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-brand-red"
            />
            <span className="text-brand-navy font-semibold text-sm">
              Email the customer this update
              {order.customer_email ? <span className="block text-brand-ink2 font-normal">{order.customer_email}</span>
                : <span className="block text-brand-ink2 font-normal">No customer email on file</span>}
            </span>
          </label>
        </div>
        <DialogFooter>
          <button
            onClick={save}
            disabled={saving}
            data-testid={`order-ship-save-${order.session_id}`}
            className="btn-focus inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save fulfillment"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl bg-white border border-brand-line p-5 shadow-card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className={`grid place-items-center w-11 h-11 rounded-xl ${accent}`}>
        <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
      </span>
    </div>
    <p className="mt-4 font-display font-black text-3xl text-brand-navy leading-none">{value}</p>
    <p className="mt-1.5 text-sm font-semibold text-brand-ink2">{label}</p>
  </div>
);

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
      onLogin(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center bg-brand-bg px-5 py-16" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo /></div>
        <form onSubmit={submit} className="rounded-3xl bg-white border border-brand-line shadow-card p-8">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand-navy">
              <Lock className="w-5 h-5 text-brand-red" strokeWidth={2.4} />
            </span>
            <h1 className="font-display font-bold text-2xl text-brand-navy">Admin Login</h1>
          </div>
          <p className="mt-2 text-brand-ink2">Sign in to view leads and orders.</p>

          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-brand-navy font-semibold">Email</Label>
              <Input id="email" type="email" data-testid="admin-email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="admin@lifebutton.org"
                className="h-13 min-h-[52px] rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-brand-navy font-semibold">Password</Label>
              <Input id="password" type="password" data-testid="admin-password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="h-13 min-h-[52px] rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy" />
            </div>
            {error && <p className="text-brand-red font-semibold text-sm" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-button"
              className="btn-focus w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-brand-red text-white font-bold text-lg hover:bg-brand-red-600 transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout, onUserUpdate }) {
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadStatus, setLeadStatus] = useState("all");
  const [leadDate, setLeadDate] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatus, setOrderStatus] = useState("all");
  const [orderDate, setOrderDate] = useState("all");
  const [leadSort, setLeadSort] = useState({ key: "created_at", dir: "desc" });
  const [orderSort, setOrderSort] = useState({ key: "created_at", dir: "desc" });

  const sortLeads = (key) => setLeadSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortOrders = (key) => setOrderSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) {
      toast.error("Session expired. Please sign in again.");
      onLogout();
      return true;
    }
    return false;
  }, [onLogout]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, o] = await Promise.all([
        api.get("/admin/summary"),
        api.get("/admin/leads"),
        api.get("/admin/orders"),
      ]);
      setSummary(s.data);
      setLeads(l.data);
      setOrders(o.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => { load(); }, [load]);

  const setLeadStatusValue = async (lead, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((x) => (x.id === lead.id ? { ...x, status } : x)));
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      api.get("/admin/summary").then(({ data }) => setSummary(data)).catch(() => {});
      toast.success(`Marked "${lead.name}" as ${status}`);
    } catch (err) {
      setLeads(prev);
      if (!handleAuthError(err)) toast.error("Could not update status.");
    }
  };

  const addNote = async (lead, text) => {
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/notes`, { text });
      setLeads((ls) => ls.map((x) => (x.id === lead.id ? { ...x, note_history: [...(x.note_history || []), data.entry] } : x)));
      toast.success("Note added to timeline");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not add note.");
    }
  };

  const updateOrder = async (order, patch) => {
    const prev = orders;
    setOrders((os) => os.map((x) => (x.session_id === order.session_id
      ? { ...x, ship_status: patch.ship_status ?? x.ship_status, tracking_number: patch.tracking_number ?? x.tracking_number }
      : x)));
    try {
      const { data } = await api.patch(`/admin/orders/${order.session_id}`, patch);
      toast.success(data.emailed ? "Saved & customer emailed" : "Fulfillment updated");
    } catch (err) {
      setOrders(prev);
      if (!handleAuthError(err)) toast.error("Could not update order.");
    }
  };

  const filteredLeads = useMemo(() => leads.filter((l) => {
    const s = leadSearch.trim().toLowerCase();
    const matchS = !s || [l.name, l.email, l.phone, l.interest].some((f) => (f || "").toLowerCase().includes(s));
    const matchStatus = leadStatus === "all" || (l.status || "new") === leadStatus;
    return matchS && matchStatus && withinRange(l.created_at, leadDate);
  }), [leads, leadSearch, leadStatus, leadDate]);

  const filteredOrders = useMemo(() => orders.filter((o) => {
    const s = orderSearch.trim().toLowerCase();
    const itemNames = (o.items || []).map((i) => i.name).join(" ");
    const city = o.shipping_address?.city || "";
    const matchS = !s || [o.session_id, itemNames, o.tracking_number, o.customer_name, o.customer_email, city].some((f) => (f || "").toLowerCase().includes(s));
    const matchStatus = orderStatus === "all" || o.payment_status === orderStatus;
    return matchS && matchStatus && withinRange(o.created_at, orderDate);
  }), [orders, orderSearch, orderStatus, orderDate]);

  const sortedLeads = useMemo(() => sortRows(filteredLeads, leadSort), [filteredLeads, leadSort]);
  const sortedOrders = useMemo(() => sortRows(filteredOrders, orderSort), [filteredOrders, orderSort]);

  const exportLeads = () => downloadCSV(
    `lifebutton-leads-${new Date().toISOString().slice(0, 10)}.csv`,
    ["Date", "Name", "Email", "Phone", "ZIP", "Interest", "Message", "Status", "Notes"],
    sortedLeads.map((l) => [fmtDate(l.created_at), l.name, l.email, l.phone, l.zip_code, l.interest, l.message, l.status || "new", noteToStr(l)]),
  );

  const exportOrders = () => downloadCSV(
    `lifebutton-orders-${new Date().toISOString().slice(0, 10)}.csv`,
    ["Date", "Items", "Amount (USD)", "Payment", "Shipping", "Tracking", "Session ID"],
    sortedOrders.map((o) => [fmtDate(o.created_at), (o.items || []).map((i) => `${i.name} x${i.quantity}`).join("; "), o.amount, o.payment_status, o.ship_status || "processing", o.tracking_number || "", o.session_id]),
  );

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      {/* top bar */}
      <div className="bg-white border-b border-brand-line">
        <div className="container-x flex items-center justify-between h-[70px]">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline px-2.5 py-1 rounded-full bg-brand-navy text-white text-xs font-bold uppercase tracking-wide">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <AccountDialog user={user} onUpdated={onUserUpdate} />
            <button onClick={load} data-testid="admin-refresh"
              className="btn-focus grid place-items-center w-11 h-11 rounded-xl border border-brand-line text-brand-navy hover:bg-brand-bg transition-colors">
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onLogout} data-testid="admin-logout"
              className="btn-focus inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-brand-navy text-white font-bold hover:bg-brand-navy-700 transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="container-x py-8 lg:py-10">
        <h1 className="font-display font-black text-3xl text-brand-navy">Dashboard</h1>
        <p className="mt-1 text-brand-ink2">Every quote and order, in one place.</p>

        {/* stats */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={Inbox} label="Total Leads" value={summary?.total_leads ?? "—"} accent="bg-brand-navy" />
          <StatCard icon={Users} label="New Leads" value={summary?.new_leads ?? "—"} accent="bg-brand-red" />
          <StatCard icon={ShoppingBag} label="Total Orders" value={summary?.total_orders ?? "—"} accent="bg-brand-navy" />
          <StatCard icon={ShoppingBag} label="Paid Orders" value={summary?.paid_orders ?? "—"} accent="bg-emerald-600" />
          <StatCard icon={DollarSign} label="Revenue" value={summary ? formatUSD(summary.revenue) : "—"} accent="bg-brand-red" />
        </div>

        {/* sales snapshot chart */}
        <div className="mt-6">
          <SalesSnapshot />
        </div>

        {/* tabs */}
        <Tabs defaultValue="leads" className="mt-8">
          <TabsList className="bg-white border border-brand-line rounded-xl p-1 h-auto">
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-lg px-5 py-2.5 font-bold data-[state=active]:bg-brand-navy data-[state=active]:text-white">
              Quote Requests ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-lg px-5 py-2.5 font-bold data-[state=active]:bg-brand-navy data-[state=active]:text-white">
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="marketing" data-testid="tab-marketing" className="rounded-lg px-5 py-2.5 font-bold data-[state=active]:bg-brand-navy data-[state=active]:text-white">
              Marketing
            </TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-5">
            <FilterBar
              prefix="leads"
              search={leadSearch} setSearch={setLeadSearch}
              status={leadStatus} setStatus={setLeadStatus} statusOptions={STATUS_OPTS_LEADS}
              dateRange={leadDate} setDateRange={setLeadDate}
              onExport={exportLeads} count={sortedLeads.length} total={leads.length}
              placeholder="Search name, email, phone…"
            />
            <div className="rounded-2xl bg-white border border-brand-line overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-brand-bg hover:bg-brand-bg">
                      <SortHeader label="Date" sortKey="created_at" sort={leadSort} onSort={sortLeads} testid="leads-sort-date" />
                      <SortHeader label="Name" sortKey="name" sort={leadSort} onSort={sortLeads} testid="leads-sort-name" />
                      <TableHead className="text-brand-navy font-bold">Contact</TableHead>
                      <TableHead className="text-brand-navy font-bold">Interest</TableHead>
                      <TableHead className="text-brand-navy font-bold">Message</TableHead>
                      <TableHead className="text-brand-navy font-bold">Status</TableHead>
                      <TableHead className="text-brand-navy font-bold">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLeads.length === 0 && !loading && (
                      <TableRow><TableCell colSpan={7} className="text-center py-12 text-brand-ink2">No matching quote requests.</TableCell></TableRow>
                    )}
                    {sortedLeads.map((l) => (
                      <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="align-top">
                        <TableCell className="text-brand-ink2 whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                        <TableCell className="font-bold text-brand-navy whitespace-nowrap">{l.name}</TableCell>
                        <TableCell>
                          <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-brand-navy hover:text-brand-red"><Mail className="w-3.5 h-3.5" />{l.email}</a>
                          <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 text-brand-ink2 hover:text-brand-red mt-1"><Phone className="w-3.5 h-3.5" />{l.phone}</a>
                        </TableCell>
                        <TableCell className="text-brand-navy">{l.interest || "—"}</TableCell>
                        <TableCell className="text-brand-ink2 max-w-[220px]"><span className="line-clamp-2">{l.message || "—"}</span></TableCell>
                        <TableCell>
                          <Select value={l.status || "new"} onValueChange={(v) => setLeadStatusValue(l, v)}>
                            <SelectTrigger data-testid={`lead-status-${l.id}`} className={`w-[130px] h-10 rounded-full border font-bold capitalize ${STATUS_STYLE[l.status || "new"]}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <NoteTimeline lead={l} onAdd={(text) => addNote(l, text)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-5">
            <FilterBar
              prefix="orders"
              search={orderSearch} setSearch={setOrderSearch}
              status={orderStatus} setStatus={setOrderStatus} statusOptions={STATUS_OPTS_ORDERS}
              dateRange={orderDate} setDateRange={setOrderDate}
              onExport={exportOrders} count={sortedOrders.length} total={orders.length}
              placeholder="Search customer, city, items, tracking…"
            />
            <div className="rounded-2xl bg-white border border-brand-line overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-brand-bg hover:bg-brand-bg">
                      <SortHeader label="Date" sortKey="created_at" sort={orderSort} onSort={sortOrders} testid="orders-sort-date" />
                      <TableHead className="text-brand-navy font-bold">Items</TableHead>
                      <SortHeader label="Amount" sortKey="amount" sort={orderSort} onSort={sortOrders} testid="orders-sort-amount" />
                      <TableHead className="text-brand-navy font-bold">Payment</TableHead>
                      <TableHead className="text-brand-navy font-bold">Fulfillment</TableHead>
                      <TableHead className="text-brand-navy font-bold">Session</TableHead>
                      <TableHead className="text-brand-navy font-bold">Detail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedOrders.length === 0 && !loading && (
                      <TableRow><TableCell colSpan={7} className="text-center py-12 text-brand-ink2">No matching orders.</TableCell></TableRow>
                    )}
                    {sortedOrders.map((o) => (
                      <TableRow key={o.session_id} data-testid={`order-row-${o.session_id}`}>
                        <TableCell className="text-brand-ink2 whitespace-nowrap">{fmtDate(o.created_at)}</TableCell>
                        <TableCell className="text-brand-navy">
                          {(o.items || []).map((it, i) => (
                            <div key={i} className="whitespace-nowrap">{it.name} × {it.quantity}</div>
                          ))}
                        </TableCell>
                        <TableCell className="font-bold text-brand-navy whitespace-nowrap">{formatUSD(o.amount)}</TableCell>
                        <TableCell>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold capitalize ${PAY_STYLE[o.payment_status] || "bg-slate-100 text-slate-600"}`}>
                            {o.payment_status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <ShipDialog order={o} onSave={(patch) => updateOrder(o, patch)} />
                          {o.tracking_number ? <p className="mt-1 text-xs text-brand-ink2 font-mono">{o.tracking_number}</p> : null}
                        </TableCell>
                        <TableCell className="text-brand-ink2 font-mono text-xs">{(o.session_id || "").slice(0, 18)}…</TableCell>
                        <TableCell>
                          <OrderDetailDialog order={o} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* MARKETING */}
          <TabsContent value="marketing" className="mt-5">
            <MarketingInsights />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(ADMIN_TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-brand-bg">
        <Loader2 className="w-10 h-10 text-brand-red animate-spin" />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} onUserUpdate={(u) => setUser((prev) => ({ ...prev, ...u }))} /> : <LoginView onLogin={setUser} />;
}
